import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { SEO } from '../components/SEO';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Switch } from '../components/ui/switch';
import { 
  ChevronLeft, ChevronRight, Plus, Loader2, Calendar as CalendarIcon, 
  Clock, Trash2, Edit, X
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Predefined colors for events
const EVENT_COLORS = [
  { name: 'Green', value: '#1a5f4a' },
  { name: 'Blue', value: '#2563eb' },
  { name: 'Red', value: '#dc2626' },
  { name: 'Purple', value: '#7c3aed' },
  { name: 'Orange', value: '#ea580c' },
  { name: 'Pink', value: '#db2777' },
  { name: 'Teal', value: '#0d9488' },
  { name: 'Amber', value: '#d97706' },
];

export const CalendarPage = () => {
  const { user, getAuthHeader } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showEventDetail, setShowEventDetail] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  
  const [eventForm, setEventForm] = useState({
    title: '',
    date: '',
    end_date: '',
    color: '#1a5f4a',
    description: '',
    all_day: true,
    time: ''
  });

  const isCommitteeOrPresident = user && (user.role === 'committee' || user.role === 'president');

  useEffect(() => {
    fetchEvents();
  }, [currentDate]);

  const fetchEvents = async () => {
    try {
      const month = currentDate.getMonth() + 1;
      const year = currentDate.getFullYear();
      const response = await axios.get(`${API}/calendar-events?month=${month}&year=${year}`);
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error('Failed to load calendar events');
    } finally {
      setLoading(false);
    }
  };

  // Calendar helpers
  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const formatDate = (date) => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  const parseDate = (dateStr) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  const getEventsForDate = (date) => {
    const dateStr = formatDate(date);
    return events.filter(event => {
      if (event.end_date) {
        return dateStr >= event.date && dateStr <= event.end_date;
      }
      return event.date === dateStr;
    });
  };

  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const openAddEventModal = (date = null) => {
    setEditMode(false);
    setEventForm({
      title: '',
      date: date ? formatDate(date) : formatDate(new Date()),
      end_date: '',
      color: '#1a5f4a',
      description: '',
      all_day: true,
      time: ''
    });
    setShowEventModal(true);
  };

  const openEditEventModal = (event) => {
    setEditMode(true);
    setSelectedEvent(event);
    setEventForm({
      title: event.title,
      date: event.date,
      end_date: event.end_date || '',
      color: event.color,
      description: event.description || '',
      all_day: event.all_day,
      time: event.time || ''
    });
    setShowEventDetail(false);
    setShowEventModal(true);
  };

  const handleEventClick = (event, e) => {
    e.stopPropagation();
    setSelectedEvent(event);
    setShowEventDetail(true);
  };

  const handleDateClick = (date) => {
    if (isCommitteeOrPresident) {
      setSelectedDate(date);
      openAddEventModal(date);
    }
  };

  const handleSaveEvent = async () => {
    if (!eventForm.title.trim()) {
      toast.error('Please enter an event title');
      return;
    }
    if (!eventForm.date) {
      toast.error('Please select a date');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        title: eventForm.title,
        date: eventForm.date,
        end_date: eventForm.end_date || null,
        color: eventForm.color,
        description: eventForm.description || null,
        all_day: eventForm.all_day,
        time: !eventForm.all_day ? eventForm.time : null
      };

      if (editMode && selectedEvent) {
        await axios.put(`${API}/calendar-events/${selectedEvent.id}`, payload, {
          headers: getAuthHeader()
        });
        toast.success('Event updated');
      } else {
        await axios.post(`${API}/calendar-events`, payload, {
          headers: getAuthHeader()
        });
        toast.success('Event created');
      }

      setShowEventModal(false);
      fetchEvents();
    } catch (error) {
      console.error('Error saving event:', error);
      toast.error(error.response?.data?.detail || 'Failed to save event');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEvent = async () => {
    if (!selectedEvent) return;

    try {
      await axios.delete(`${API}/calendar-events/${selectedEvent.id}`, {
        headers: getAuthHeader()
      });
      toast.success('Event deleted');
      setShowEventDetail(false);
      fetchEvents();
    } catch (error) {
      console.error('Error deleting event:', error);
      toast.error('Failed to delete event');
    }
  };

  // Render calendar grid
  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];
    const today = new Date();
    const todayStr = formatDate(today);

    // Day headers
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    // Empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(
        <div key={`empty-${i}`} className="h-24 sm:h-32 bg-stone-50 border border-stone-200"></div>
      );
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
      const dateStr = formatDate(date);
      const dayEvents = getEventsForDate(date);
      const isToday = dateStr === todayStr;

      days.push(
        <div
          key={day}
          className={`h-24 sm:h-32 border border-stone-200 p-1 overflow-hidden cursor-pointer transition-colors hover:bg-stone-50 ${
            isToday ? 'bg-primary/5 border-primary' : 'bg-white'
          }`}
          onClick={() => handleDateClick(date)}
          data-testid={`calendar-day-${dateStr}`}
        >
          <div className={`text-sm font-medium mb-1 ${isToday ? 'text-primary' : 'text-stone-700'}`}>
            {day}
          </div>
          <div className="space-y-0.5 overflow-y-auto max-h-16 sm:max-h-24">
            {dayEvents.slice(0, 3).map((event) => (
              <div
                key={event.id}
                className="text-xs px-1.5 py-0.5 rounded truncate cursor-pointer hover:opacity-80 transition-opacity"
                style={{ backgroundColor: event.color, color: 'white' }}
                onClick={(e) => handleEventClick(event, e)}
                title={event.title}
                data-testid={`event-${event.id}`}
              >
                {!event.all_day && event.time && (
                  <span className="opacity-75 mr-1">{event.time}</span>
                )}
                {event.title}
              </div>
            ))}
            {dayEvents.length > 3 && (
              <div className="text-xs text-stone-500 px-1">
                +{dayEvents.length - 3} more
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-7">
        {/* Day headers */}
        {dayNames.map((name) => (
          <div
            key={name}
            className="h-10 flex items-center justify-center bg-primary text-white font-medium text-sm"
          >
            {name}
          </div>
        ))}
        {days}
      </div>
    );
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  if (loading) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <SEO 
        title="Calendar"
        description="View the Manitoulin Horse Enthusiasts Club calendar for upcoming horse shows, clinics, meetups, and club activities."
        url="/calendar"
      />
      <div className="bg-secondary py-8 border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="font-display text-3xl font-bold text-stone-900">Club Calendar</h1>
              <p className="text-stone-600 mt-1">View upcoming events and activities</p>
            </div>
            {isCommitteeOrPresident && (
              <Button
                onClick={() => openAddEventModal()}
                className="bg-primary hover:bg-primary/90"
                data-testid="add-event-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Event
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
        <Card className="border-stone-200">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Button variant="outline" size="icon" onClick={prevMonth} data-testid="prev-month-btn">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="icon" onClick={nextMonth} data-testid="next-month-btn">
                  <ChevronRight className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={goToToday}>
                  Today
                </Button>
              </div>
              <CardTitle className="font-display text-xl sm:text-2xl">
                {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
              </CardTitle>
              <div className="w-24"></div> {/* Spacer for centering */}
            </div>
          </CardHeader>
          <CardContent className="p-0 sm:p-2">
            <div className="border border-stone-200 rounded-lg overflow-hidden">
              {renderCalendar()}
            </div>
          </CardContent>
        </Card>

        {/* Legend */}
        <div className="mt-6 flex flex-wrap gap-4 justify-center">
          {isCommitteeOrPresident && (
            <p className="text-sm text-stone-500 w-full text-center">
              Click on any date to add an event
            </p>
          )}
        </div>
      </div>

      {/* Add/Edit Event Modal */}
      <Dialog open={showEventModal} onOpenChange={setShowEventModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              {editMode ? 'Edit Event' : 'Add Event'}
            </DialogTitle>
            <DialogDescription>
              {editMode ? 'Update the event details' : 'Create a new calendar event'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="title">Event Title *</Label>
              <Input
                id="title"
                value={eventForm.title}
                onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                placeholder="Enter event title"
                data-testid="event-title-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="date">Start Date *</Label>
                <Input
                  id="date"
                  type="date"
                  value={eventForm.date}
                  onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })}
                  data-testid="event-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end_date">End Date</Label>
                <Input
                  id="end_date"
                  type="date"
                  value={eventForm.end_date}
                  onChange={(e) => setEventForm({ ...eventForm, end_date: e.target.value })}
                  min={eventForm.date}
                  data-testid="event-end-date-input"
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="all_day">All Day Event</Label>
              <Switch
                id="all_day"
                checked={eventForm.all_day}
                onCheckedChange={(checked) => setEventForm({ ...eventForm, all_day: checked })}
                data-testid="event-all-day-switch"
              />
            </div>

            {!eventForm.all_day && (
              <div className="space-y-2">
                <Label htmlFor="time">Time</Label>
                <Input
                  id="time"
                  type="time"
                  value={eventForm.time}
                  onChange={(e) => setEventForm({ ...eventForm, time: e.target.value })}
                  data-testid="event-time-input"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label>Color</Label>
              <div className="flex flex-wrap gap-2">
                {EVENT_COLORS.map((color) => (
                  <button
                    key={color.value}
                    type="button"
                    className={`w-8 h-8 rounded-full border-2 transition-transform hover:scale-110 ${
                      eventForm.color === color.value ? 'border-stone-900 scale-110' : 'border-transparent'
                    }`}
                    style={{ backgroundColor: color.value }}
                    onClick={() => setEventForm({ ...eventForm, color: color.value })}
                    title={color.name}
                    data-testid={`color-${color.name.toLowerCase()}`}
                  />
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={eventForm.description}
                onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                placeholder="Add event description (optional)"
                rows={3}
                data-testid="event-description-input"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowEventModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveEvent}
              disabled={saving}
              className="bg-primary hover:bg-primary/90"
              data-testid="save-event-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : editMode ? (
                'Update Event'
              ) : (
                'Create Event'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Event Detail Modal */}
      <Dialog open={showEventDetail} onOpenChange={setShowEventDetail}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div
                  className="w-4 h-4 rounded-full flex-shrink-0"
                  style={{ backgroundColor: selectedEvent?.color }}
                />
                <DialogTitle className="font-display text-xl">
                  {selectedEvent?.title}
                </DialogTitle>
              </div>
            </div>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="flex items-center gap-2 text-stone-600">
              <CalendarIcon className="w-4 h-4" />
              <span>
                {selectedEvent?.date && new Date(selectedEvent.date + 'T00:00:00').toLocaleDateString('en-CA', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
                {selectedEvent?.end_date && selectedEvent.end_date !== selectedEvent.date && (
                  <> - {new Date(selectedEvent.end_date + 'T00:00:00').toLocaleDateString('en-CA', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}</>
                )}
              </span>
            </div>

            {!selectedEvent?.all_day && selectedEvent?.time && (
              <div className="flex items-center gap-2 text-stone-600">
                <Clock className="w-4 h-4" />
                <span>{selectedEvent.time}</span>
              </div>
            )}

            {selectedEvent?.description && (
              <div className="pt-2 border-t border-stone-200">
                <p className="text-stone-700 whitespace-pre-wrap">{selectedEvent.description}</p>
              </div>
            )}
          </div>

          {isCommitteeOrPresident && (
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={handleDeleteEvent}
                data-testid="delete-event-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
              <Button
                onClick={() => openEditEventModal(selectedEvent)}
                className="bg-primary hover:bg-primary/90"
                data-testid="edit-event-btn"
              >
                <Edit className="w-4 h-4 mr-2" />
                Edit
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default CalendarPage;
